import { useEffect, useRef, useState } from "react";

import { BOOKING_DONE_CODE, PAY_SUCCESS_CODE } from "../constants";
import { getOrderStatus } from "../protocols";

/**
 * Статус заказа после оплаты картой без перезагрузки страницы.
 *
 * - paySuccess («оплачено, билеты выписываются») — раз в 15 с спрашиваем статус, пока выписка не закончится;
 * - клиент вернулся со страницы оплаты, а колбэк банка ещё не пришёл (заказ всё ещё bookingDone) — показываем
 *   «проверяем оплату» и спрашиваем раз в 5 с, не дольше 2 минут.
 *
 * Опрос — лёгкий /order-status (локальная база бека, без запроса в ads_v1), только пока вкладка видна, с потолком
 * по времени; полную карточку заказа перечитываем один раз — когда статус сменился (onStatusChange).
 */

const STORAGE_PREFIX = "payment_started:";
const MARK_TTL_MS = 30 * 60 * 1000;
const CHECKING_INTERVAL_MS = 5 * 1000;
const CHECKING_LIMIT_MS = 2 * 60 * 1000;
const PAID_INTERVAL_MS = 15 * 1000;
const PAID_LIMIT_MS = 15 * 60 * 1000;

// sessionStorage может быть недоступен (приватный режим, запрет cookies) — тогда просто без «проверяем оплату»
const readMark = orderId => {
  try {
    return Number(window.sessionStorage.getItem(STORAGE_PREFIX + orderId)) || null;
  } catch (e) {
    return null;
  }
};

const clearMark = orderId => {
  try {
    window.sessionStorage.removeItem(STORAGE_PREFIX + orderId);
  } catch (e) {
    // нет доступа к хранилищу — нечего чистить
  }
};

/** Клиент уходит на страницу оплаты: по возврату до колбэка банка покажем «проверяем оплату», а не форму оплаты. */
export const markPaymentStarted = orderId => {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + orderId, String(Date.now()));
  } catch (e) {
    // нет доступа к хранилищу — без «проверяем оплату»
  }
};

const usePaymentStatusWatch = ({ order, onStatusChange }) => {
  const [checkingPayment, setCheckingPayment] = useState(false);
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const orderId = order?.order_id;
  const sessionId = order?.session_id;
  const bookingStatus = order?.booking_status;

  useEffect(() => {
    if (!orderId || !sessionId) return undefined;

    const startedAt = readMark(orderId);
    let mode = null;

    if (bookingStatus === PAY_SUCCESS_CODE) {
      mode = "paid";
      clearMark(orderId);
    } else if (bookingStatus === BOOKING_DONE_CODE && startedAt && Date.now() - startedAt < MARK_TTL_MS) {
      mode = "checking";
    } else if (bookingStatus !== BOOKING_DONE_CODE) {
      clearMark(orderId);
    }

    setCheckingPayment(mode === "checking");
    if (!mode) return undefined;

    const interval = mode === "paid" ? PAID_INTERVAL_MS : CHECKING_INTERVAL_MS;
    const deadline = Date.now() + (mode === "paid" ? PAID_LIMIT_MS : CHECKING_LIMIT_MS);
    let stopped = false;
    let timer = null;

    const stop = () => {
      stopped = true;
      clearTimeout(timer);
    };

    const tick = async () => {
      if (stopped) return;

      if (Date.now() > deadline) {
        stop();
        if (mode === "checking") {
          // колбэк так и не пришёл — возвращаем обычную страницу (оплата могла не пройти)
          clearMark(orderId);
          setCheckingPayment(false);
        }
        return;
      }

      if (document.visibilityState === "visible") {
        try {
          const data = await getOrderStatus({ order_id: orderId, session_id: sessionId });

          if (!stopped && data && !data.error_code && data.booking_status && data.booking_status !== bookingStatus) {
            stop();
            onStatusChangeRef.current?.();
            return;
          }
        } catch (e) {
          // сеть или лимит запросов — спросим в следующий раз
        }
      }

      if (!stopped) timer = setTimeout(tick, interval);
    };

    timer = setTimeout(tick, interval);

    return stop;
  }, [orderId, sessionId, bookingStatus]);

  return { checkingPayment };
};

export default usePaymentStatusWatch;
